import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AlertHandlerService } from '@finxone-platform/shared/services';

/**
 * Top-level state slices to omit when exporting/serializing the whole state tree.
 * Add/remove keys here to change what's excluded.
 */
const EXCLUDED_STATE_KEYS: readonly string[] = [
  'apiLoading',
  'globalThemeState',
  'projectSettings',
  'copyPasteState',
  'appGridState',
  'pageContextState',
  'ImageActionState',
  'companyDetail',
  'ProjectNotificationsState',
  'ProjectAlerts',
  'bulkPaymentHistory',
  'paymentRequest',
  'bulkPaymentList',
  'teamMembers',
  'progressBarWorkflowItems',
  'workflowProjects',
  'payment',
  'monthlysummary',
  'appData',
];

@Injectable({
  providedIn: 'root',
})
export class EditMockDataService {
  private store = inject(Store);
  alertService = inject(AlertHandlerService);

  public getFilteredStateSnapshot(): Record<string, unknown> {
    const snapshot = this.store.snapshot();
    return this.omitKeys(snapshot, EXCLUDED_STATE_KEYS);
  }

  public getFilteredStateAsFormattedJson(indent = 2): string {
    return JSON.stringify(this.getFilteredStateSnapshot(), null, indent);
  }

  private omitKeys(obj: Record<string, unknown>, keysToOmit: readonly string[]): Record<string, unknown> {
    const excludeSet = new Set(keysToOmit);
    return Object.fromEntries(Object.entries(obj).filter(([key]) => !excludeSet.has(key)));
  }

  public saveEditedMockData(editedJson: string): boolean {
    const referenceSnapshot = this.getFilteredStateSnapshot();
    const result = this.validateEditedState(editedJson, referenceSnapshot);

    if (!result.valid) {
      this.alertService.showAlertFn(
        'error',
        result.errors[0] || 'Invalid JSON or state structure. Please check your edits.',
      );
      return false;
    } else {
      const fullSnapshot = this.store.snapshot();
      this.store.reset({ ...fullSnapshot, ...result.data });
      return true;
    }
  }

  private validateEditedState(
    editedJson: string,
    referenceSnapshot: Record<string, unknown>,
    disallowExtraKeys = false,
  ): { valid: boolean; errors: string[]; data: Record<string, unknown> | null } {
    let parsed: unknown;

    try {
      parsed = JSON.parse(editedJson);
    } catch (e) {
      return {
        valid: false,
        errors: [`Invalid JSON: ${(e as Error).message}`],
        data: null,
      };
    }

    const errors: string[] = [];
    this.diff(referenceSnapshot, parsed, '', errors, disallowExtraKeys);

    return errors.length > 0
      ? { valid: false, errors, data: null }
      : { valid: true, errors: [], data: parsed as Record<string, unknown> };
  }

  private diff(
    reference: unknown,
    edited: unknown,
    path: string,
    errors: string[],
    disallowExtraKeys: boolean,
  ): void {
    // Dates become ISO strings once the state is round-tripped through JSON,
    // so a Date in the reference should accept a valid date string in the
    // edited value rather than being flagged as a type mismatch.
    if (reference instanceof Date) {
      if (edited === null || edited === undefined) {
        errors.push(`${path || '(root)'}: missing property (expected date string)`);
        return;
      }
      if (typeof edited !== 'string' || isNaN(Date.parse(edited))) {
        errors.push(`${path || '(root)'}: expected a valid date string, got ${this.typeOf(edited)}`);
      }
      return;
    }

    const referenceType = this.typeOf(reference);
    const editedType = this.typeOf(edited);

    // null/undefined in the reference means we can't meaningfully check
    // type or shape further - anything the user puts here is accepted.
    if (referenceType === 'null' || referenceType === 'undefined') {
      return;
    }

    if (editedType === 'undefined') {
      errors.push(`${path || '(root)'}: missing property (expected ${referenceType})`);
      return;
    }

    // Allow the edited value to be null even if reference wasn't -
    // remove this block if null should always be rejected.
    if (editedType === 'null') {
      return;
    }

    if (referenceType !== editedType) {
      errors.push(`${path || '(root)'}: expected ${referenceType}, got ${editedType}`);
      return;
    }

    if (referenceType === 'array') {
      const refArr = reference as unknown[];
      const editedArr = edited as unknown[];

      // Compare each edited element against the reference element at the
      // SAME index - array items commonly have differing shapes (partial
      // data, optional fields), so using a single "template" element for
      // every index produces false positives. Falls back to the last
      // reference element's shape for indexes beyond the reference length
      // (e.g. if the user added new items), and skips validation entirely
      // if the reference array is empty.
      editedArr.forEach((item, index) => {
        const refElement = index < refArr.length ? refArr[index] : refArr[refArr.length - 1];
        if (refElement !== undefined) {
          this.diff(refElement, item, `${path}[${index}]`, errors, disallowExtraKeys);
        }
      });
      return;
    }

    if (referenceType === 'object') {
      const refObj = reference as Record<string, unknown>;
      const editedObj = edited as Record<string, unknown>;

      for (const key of Object.keys(refObj)) {
        const childPath = path ? `${path}.${key}` : key;
        this.diff(refObj[key], editedObj[key], childPath, errors, disallowExtraKeys);
      }

      if (disallowExtraKeys) {
        for (const key of Object.keys(editedObj)) {
          if (!(key in refObj)) {
            const childPath = path ? `${path}.${key}` : key;
            errors.push(`${childPath}: unexpected property not present in original state`);
          }
        }
      }
      return;
    }

    // primitives (string, number, boolean) - type already matched above.
  }

  private typeOf(value: unknown): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  }
}
